/**
 * Canonical brand-slug generation — the single source of truth for turning a
 * Typesense brand name ("ST. IDES") into a URL segment ("st-ides").
 *
 * Why this exists
 * ---------------
 * There used to be two competing implementations, and they disagreed:
 *
 *   universal-product-card.tsx  brand.toLowerCase()
 *                                    .replace(/\s+/g, '-')
 *                                    .replace(/[^a-z0-9-]/g, '')   -> "st-ides"
 *
 *   sitemap / brand index /     brand.toLowerCase()
 *   filter menu / resolver           .replace(/\s+/g, '-')         -> "st.-ides"
 *
 * The resolver (`findMatchingBrand`) used the second form, so every product
 * card for a brand containing `.`, `'` or `&` linked to a 404. Semrush picked
 * up /brand/st-ides and /brand/beard-bros; the same fault affected DR. NORM'S,
 * HENRY'S ORIGINAL, NOT YOUR FATHER'S, POUNDCAKE'S FARMS, PAPA & BARKLEY,
 * HERITAGE HASH CO. and WE DON'T SMOKE THE SAME.
 *
 * Rather than align on the punctuation-preserving form, we canonicalise on the
 * clean one. `/brand/papa-&-barkley` and `/brand/dr.-norm's` are legal but
 * fragile — they need escaping in XML, encoding in HTML and quoting in shells,
 * and they read badly when shared.
 *
 * Rules
 * -----
 *   lowercase · apostrophes dropped (not hyphenated, so "DR. NORM'S" becomes
 *   "dr-norms" rather than "dr-norm-s") · "&" becomes "and" · any other
 *   non-alphanumeric run becomes a single hyphen · leading/trailing hyphens
 *   trimmed.
 *
 * Verified against all 101 live brands: 8 slugs change, 0 collisions.
 *
 * Legacy URLs are still resolvable via `legacyBrandSlug` so already-indexed
 * links keep working; the brand route 301s them to the canonical form.
 */

/** Canonical slug for a brand name. Use this everywhere a brand URL is built. */
export function toBrandSlug(brandName: string): string {
  return brandName
    .toLowerCase()
    .replace(/['’]/g, '') // drop apostrophes rather than turning them into hyphens
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * The historic slug form — spaces to hyphens, punctuation left intact
 * ("st.-ides"). Only for recognising already-indexed URLs so they can be
 * redirected. Never generate new links with this.
 */
export function legacyBrandSlug(brandName: string): string {
  return brandName.toLowerCase().replace(/\s+/g, '-');
}

/**
 * The pre-existing product-card form — punctuation stripped without collapsing
 * the resulting gaps ("papa--barkley"). These URLs were emitted site-wide and
 * are likely indexed, so they must resolve too.
 */
export function legacyStrippedBrandSlug(brandName: string): string {
  return brandName
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/** True if `slug` identifies `brandName` in any form we have ever published. */
export function brandSlugMatches(brandName: string, slug: string): boolean {
  const candidate = slug.toLowerCase();
  return (
    toBrandSlug(brandName) === candidate ||
    legacyBrandSlug(brandName) === candidate ||
    legacyStrippedBrandSlug(brandName) === candidate
  );
}
